import React, {useCallback, useEffect, Suspense, useState} from 'react';
import {pendoService} from '../services/pendoService.web';
import {showErrorToast} from '../store/errorSlice';
import {NavigationContainer} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import {useDispatch} from 'react-redux';
import {setNovaAIEnabled} from '../../shared/store/novaAISlice';
import {navigationRef} from '../navigation/navigationRef';
import {setSessionExpiredCallback} from '../../features/auth/api/axiosInstance';
import {timeZoneStorage} from '../../features/auth/api/timeZoneStorage';
import {TIMEZONE_STORAGE_KEY} from '../store/userSlice';
import {
  refreshAccessToken,
  signoutWithBrowser,
  userManager,
} from '../../features/auth/services/authProvider';
import {validateAgencyName} from '../../features/auth/services/validateAgencyNameService';
import {agencyStorage} from '../../features/auth/api/agencyStorage';
import {getWebAgency} from '../config/agencyUrl';
import {tabManager} from '../services/tabManager.web';
import {storageService} from '../services/storageService';
import secureStorage from '../services/secureStorage';
import {setLanguage, LANGUAGE_STORAGE_KEY} from '../store/languageSlice';
import {setColorMode} from '../store/themeSlice';
import type {ColorMode} from '../theme/clientThemes/types';
import {
  setShiftStarted,
  SHIFT_STARTED_KEY,
} from '../store/shiftManagementSlice';
import {
  setSelectedShop,
  SHOP_STORAGE_KEY,
  SelectedShopState,
} from '../store/shopSlice';
import {
  setSelectedTechnicianData,
  setCreatedUserId,
  TECHNICIAN_STORAGE_KEY,
  CREATED_USER_ID_KEY,
  SelectedTechnician,
  DEFAULT_STORE_ROOM_KEY,
  setDefaultStoreRoom,
} from '../store/technicianSlice';
import {AUTH_USER_ID_KEY} from '../../features/auth/api/userIdStorage';
import {setActiveTab, MENU_ACTIVE_TAB_KEY} from '../store/menuSlice';
import {
  setSelectedRepairId,
  REPAIR_ID_KEY,
  setShowRepairHistory,
  SHOW_REPAIR_HISTORY_KEY,
} from '../store/repairSlice';
import {setShowQuickAssistScreen, setQuickAssistShopId, SHOW_QUICK_ASSIST_KEY, QUICK_ASSIST_SHOP_ID_KEY} from '../store/quickAssistSlice';
import {Header} from '../components/layout';
import {ActivityIndicator, View} from 'react-native';
import {useScrollbarTheme} from '../hooks/useScrollbarTheme';

// §2.1 — Route-level code splitting: each screen is loaded on demand.
// Named exports from barrel files use the .then(m => ({ default: m.X })) pattern.
const AgencySelectionScreen = React.lazy(() =>
  import('../../features/auth').then(m => ({default: m.AgencySelectionScreen})),
);
const MainLayoutScreen = React.lazy(
  () =>
    import('../../features/layout/screens/MainLayoutScreen/MainLayoutScreen'),
);
const SignatureScreen = React.lazy(
  () => import('../../features/signature/screens/SignatureScreen'),
);
const RepairDetailsScreen = React.lazy(
  () =>
    import(
      '../../features/repairDetails/screens/RepairDetailsScreen/RepairDetailsScreen'
    ),
);
const WorkOrdersScreen = React.lazy(
  () =>
    import(
      '../../features/dashboard/screens/WorkOrdersScreen/WorkOrdersScreen'
    ),
);
const PartsScreen = React.lazy(
  () => import('../../features/parts/screens/PartsScreen'),
);
const ShopHomePage = React.lazy(
  () => import('../../features/shop/screens/ShopHomePage'),
);
const HelpScreen = React.lazy(
  () => import('@features/help/screens/HelpScreen.web'),
);

export type RootStackParamList = {
  AgencySelectionScreen: undefined;
  ShopHomePage: undefined;
  MainLayoutScreen: undefined;
  RegisterUser: undefined;
  SignatureScreen: undefined;
  vinScanner: undefined;
  workOrdersScreen: undefined;
  partsScreen: undefined;
  RepairDetailsScreen: undefined;
  HelpScreen: undefined;
};

const Stack = createStackNavigator<RootStackParamList>();

/** Module-level header components to avoid S6478 inline component definitions. */
const AgencySelectionHeaderWeb = () => <Header showLanguageCTA />;
const ShopHomePageHeaderWeb = () => (
  <Header showHelpCTA showLanguageCTA showLogoutCTA showSelectedShop />
);

/** Full-screen centered spinner shown while lazy chunks load */
function ScreenFallback() {
  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
      }}>
      <ActivityIndicator size="large" color="#0071C5" />
    </View>
  );
}

// ── Module-level helpers (extracted to reduce bootstrap / handleNavigationReady CC) ──

async function rehydrateAgencyStorageWeb(): Promise<void> {
  if (!agencyStorage.get()) {
    const persistedAgencyId = await storageService.getAgencyCode();
    if (persistedAgencyId) {
      agencyStorage.set(persistedAgencyId);
      console.log(
        '[AppNavigator.web] Rehydrated agencyStorage from secureStorage:',
        persistedAgencyId,
      );
    }
  }
}

async function handleAgencyFromUrl(dispatch: any): Promise<void> {
  // ── Agency-from-subdomain detection ──────────────────────────────────
  // Read the subdomain from window.location.hostname and treat it as the
  // agency name.
  //
  // Production examples:
  //   transit-dev.fastertech.ai        →  agencyName = "transit-dev"
  //   supporttest.fastertechpreprod.ai  →  agencyName = "supporttest"
  //
  // Local-dev fallback:
  //   localhost has no subdomain, so the env var DEV_AGENCY_NAME is used
  //   instead when running on localhost / 127.0.0.1 / 0.0.0.0.
  //   Set DEV_AGENCY_NAME=transit-dev in .env.web to mirror production.
  //
  // To revert to the old URL-path-segment approach, replace this block
  // with the "Agency-from-URL detection" block preserved in git history
  // (search for "Agency-from-URL detection" in the git log).
  //
 // NOTE: validateAgencyName is called on EVERY bootstrap (page load / refresh)
      // regardless of whether agencyStorage is already populated.  This ensures
      // `isNovaAIEnabled` (and any other BFF feature flags returned here) are
      // always fresh — if the BFF flips the flag between sessions the user sees
      // the correct value immediately on next load rather than reading a stale
      // cached value.  agencyStorage.set() is guarded separately so the
      // X-Agency-Id header population still short-circuits on returning visits.
      {
    const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(
      window.location.hostname,
    );
    // 1. Subdomain of the real hostname (deployed path).
    // 2. DEV_AGENCY_NAME env var on localhost (dev path).
    // getWebAgency() is the same function agencyUrl.ts uses to decide which
    // host the API and Nova calls go to, so the agency validated here, the
    // one sent as X-Agency-Id and the one in the URL can never differ.
    const subdomainCandidate = getWebAgency();

    if (subdomainCandidate) {
      console.log(
        isLocalhost
          ? `[AppNavigator.web] localhost — using DEV_AGENCY_NAME as agency: ${subdomainCandidate}`
          : `[AppNavigator.web] Detected agency name from subdomain: ${subdomainCandidate}`,
      );
      try {
        const validationResult = await validateAgencyName(subdomainCandidate);
        if (validationResult.isValid) {
          // Only populate agencyStorage / storageService when not already set
              // (avoids redundant writes on every page refresh while still
              // ensuring X-Agency-Id is present from the very first request).
              if (!agencyStorage.get()) {
                agencyStorage.set(subdomainCandidate);
                await storageService.setAgencyCode(subdomainCandidate);
                console.log(
                  '[AppNavigator.web] Agency validated — stored as X-Agency-Id:',
                  subdomainCandidate,
                );
              }
              // Always dispatch the latest isNovaAIEnabled from the BFF so the
              // flag reflects any server-side change since the last page load.
              dispatch(setNovaAIEnabled(validationResult.isNovaAIEnabled ?? false));
          console.log(
            '[AppNavigator.web] isNovaAIEnabled:',
                validationResult.isNovaAIEnabled ?? false,
          );
        } else {
          console.warn(
            '[AppNavigator.web] Agency name is invalid:',
            subdomainCandidate,
          );
        }
      } catch (e) {
        console.warn('[AppNavigator.web] Failed to validate agency name:', e);
      }
    }
  }
}

function hydrateShopStateWeb(shopRaw: string | null, dispatch: any): void {
  if (shopRaw) {
    try {
      dispatch(setSelectedShop(JSON.parse(shopRaw) as SelectedShopState));
    } catch {
      /* ignore */
    }
  }
}

function hydrateTechnicianStateWeb(
  technicianRaw: string | null,
  dispatch: any,
): void {
  if (technicianRaw) {
    try {
      dispatch(
        setSelectedTechnicianData(
          JSON.parse(technicianRaw) as SelectedTechnician,
        ),
      );
    } catch {
      /* ignore */
    }
  }
}

async function hydrateWebReduxState(dispatch: any): Promise<void> {
  const {userIdStorage} = require('../../features/auth/api/userIdStorage');
  const [
    shopRaw,
    technicianRaw,
    menuTab,
    repairId,
    createdUserIdRaw,
    shiftStartedRaw,
    defaultStoreroom,
    roleRaw,
    authUserIdRaw,
    showRepairHistoryRaw,
    maintShopRaw,
    showQuickAssistRaw,
    quickAssistShopIdRaw,
  ] = await Promise.all([
    secureStorage.getItem(SHOP_STORAGE_KEY),
    secureStorage.getItem(TECHNICIAN_STORAGE_KEY),
    secureStorage.getItem(MENU_ACTIVE_TAB_KEY),
    secureStorage.getItem(REPAIR_ID_KEY),
    secureStorage.getItem(CREATED_USER_ID_KEY),
    secureStorage.getItem(SHIFT_STARTED_KEY),
    secureStorage.getItem(DEFAULT_STORE_ROOM_KEY),
    secureStorage.getItem('USER_ROLE_KEY'),
    secureStorage.getItem(AUTH_USER_ID_KEY),
    secureStorage.getItem(SHOW_REPAIR_HISTORY_KEY),
    secureStorage.getItem('MAINT_SHOP_KEY'),
    secureStorage.getItem(SHOW_QUICK_ASSIST_KEY),
    secureStorage.getItem(QUICK_ASSIST_SHOP_ID_KEY),
  ]);
  if (authUserIdRaw && authUserIdRaw !== '0') {
    userIdStorage.set(Number(authUserIdRaw));
    console.log(
      '[AppNavigator.web] Rehydrated userIdStorage (auth user) for X-UserId:',
      authUserIdRaw,
    );
  }
  if (roleRaw) {
    userIdStorage.setRole(roleRaw);
    console.log('[AppNavigator.web] Rehydrated userIdStorage role:', roleRaw);
  }
  if (maintShopRaw) {
    userIdStorage.setMaintShop(maintShopRaw);
    console.log('[AppNavigator.web] Rehydrated userIdStorage maintShop');
  }
  hydrateShopStateWeb(shopRaw, dispatch);
  hydrateTechnicianStateWeb(technicianRaw, dispatch);
  if (menuTab) {
    dispatch(setActiveTab(menuTab));
  }
  if (repairId) {
    dispatch(setSelectedRepairId(repairId));
  }
  if (createdUserIdRaw && createdUserIdRaw !== '0') {
    dispatch(setCreatedUserId(createdUserIdRaw));
  }
  if (shiftStartedRaw === 'true') {
    dispatch(setShiftStarted(true));
  }
  if (defaultStoreroom) {
    dispatch(setDefaultStoreRoom(defaultStoreroom));
  }
  if (showRepairHistoryRaw === 'true') {
    dispatch(setShowRepairHistory(true));
  }
  if (showQuickAssistRaw === 'true') {
    dispatch(setShowQuickAssistScreen(true));
  }
  if (quickAssistShopIdRaw) {
    const shopId = Number(quickAssistShopIdRaw);
    if (shopId > 0) { dispatch(setQuickAssistShopId(shopId)); }
  }
  const persistedTimezone = await secureStorage.getItem(TIMEZONE_STORAGE_KEY);
  if (persistedTimezone) {
    timeZoneStorage.set(persistedTimezone);
    console.log(
      '[AppNavigator.web] Rehydrated timeZoneStorage:',
      persistedTimezone,
    );
  }
}

interface OidcUserShape {
  access_token?: string;
  expires_at?: number;
}

/**
 * Probes the live OIDC/TTAuth session and persists the token.
 * Returns the access token string or null when session is absent/expired.
 */
async function probeOidcSession(
  userManagerRef: any,
  storageServiceRef: typeof storageService,
): Promise<string | null> {
  const oidcUser = await (
    userManagerRef as unknown as {getUser(): Promise<OidcUserShape | null>}
  ).getUser();
  const now = Math.floor(Date.now() / 1000);
  if (
    !oidcUser?.access_token ||
    !oidcUser.expires_at ||
    oidcUser.expires_at <= now
  ) {
    return null;
  }
  if (oidcUser.access_token === 'ttauth-cookie-session') {
    await storageServiceRef.setAuthToken('ttauth-cookie-session');
    console.log(
      '[AppNavigator.web] TTAuth cookie session alive — sentinel stored',
    );
  } else {
    await storageServiceRef.setAuthToken(oidcUser.access_token);
    console.log('[AppNavigator.web] Live auth session — fresh token stored');
  }
  return oidcUser.access_token;
}

async function migrateLocalStorageKeys(): Promise<void> {
  const MIGRATE_KEYS = [
    'AUTH_TOKEN',
    'REFRESH_TOKEN',
    'LANGUAGE',
    'LOGIN_HINT',
    'FORCE_REAUTH',
    SHOP_STORAGE_KEY,
    TECHNICIAN_STORAGE_KEY,
    CREATED_USER_ID_KEY,
    MENU_ACTIVE_TAB_KEY,
    REPAIR_ID_KEY,
    'OPEN_VOICE_MODAL_ON_RETURN',
    LANGUAGE_STORAGE_KEY,
    SHIFT_STARTED_KEY,
    'selectedShopId',
    'selectedShopName',
    'selectedTechnicianId',
    'selectedTechnicianName',
    'activeTab',
    'shiftStarted',
  ];
  await Promise.all(
    MIGRATE_KEYS.map(async key => {
      const localValue = localStorage.getItem(key);
      if (localValue) {
        const secureValue = await secureStorage.getItem(key);
        if (!secureValue) {
          await secureStorage.setItem(key, localValue);
        }
        localStorage.removeItem(key);
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(key);
      }
    }),
  );
}

/** Recursive rAF navigator — avoids a nested arrow inside handleNavigationReady. */
function navigateToShopWhenReady(navRef: typeof navigationRef): void {
  if (navRef.current?.isReady()) {
    navRef.current.navigate('ShopHomePage' as never);
  } else {
    requestAnimationFrame(() => navigateToShopWhenReady(navRef));
  }
}

/**
 * Checks whether a JUST_LOGGED_OUT session flag is set, then probes the live
 * OIDC/TTAuth session if not. Returns the resolved token (or the original
 * existingToken unchanged when the probe is skipped / fails).
 * Extracted from bootstrap to reduce its Cognitive Complexity.
 */
async function probeBootstrapOidcSession(
  existingToken: string | null,
): Promise<string | null> {
  let justLoggedOut = false;
  try {
    justLoggedOut = sessionStorage.getItem('JUST_LOGGED_OUT') === '1';
  } catch {
    /* sessionStorage unavailable */
  }

  if (justLoggedOut) {
    return existingToken;
  }

  try {
    const probed = await probeOidcSession(userManager, storageService);
    if (probed) {
      const currentPath = globalThis.window.location.pathname.replace(
        /\/$/,
        '',
      );
      if (currentPath === '') {
        globalThis.window.history.replaceState(null, '', '/ShopHomePage');
        console.log(
          '[AppNavigator.web] bootstrap: URL rewritten to /ShopHomePage',
        );
      }
      return probed;
    } else if (existingToken) {
      console.warn(
        '[AppNavigator.web] bootstrap: stored token is stale — clearing token',
      );
      await storageService.removeAuthToken();
      return null;
    }
  } catch (e) {
    console.warn(
      '[AppNavigator.web] bootstrap: getUser() probe failed (non-blocking):',
      e,
    );
  }
  return existingToken;
}

/** Dispatches a pending success toast that was stored in sessionStorage before a page reload. */
function dispatchPendingSuccessToast(dispatch: any): void {
  try {
    const pendingToastMsg =
      typeof sessionStorage === 'undefined'
        ? null
        : sessionStorage.getItem('PENDING_SUCCESS_TOAST');
    if (pendingToastMsg) {
      sessionStorage.removeItem('PENDING_SUCCESS_TOAST');
      dispatch(
        showErrorToast({toastMessage: pendingToastMsg, successToast: true}),
      );
    }
  } catch {
    /* ignore — sessionStorage may be unavailable */
  }
}
/**
 * AppNavigator
 * Defines the app's main navigation stack for the web.
 * Uses React Navigation's Stack Navigator.
 */

const linking = {
  prefixes: ['http://localhost:3000', 'https://localhost:3000'],
  config: {
    screens: {
      ShopHomePage: 'ShopHomePage',
      AgencySelectionScreen: {
        path: '',
        exact: true,
      },
      // MainLayoutScreen is now URL-tracked so a refresh restores the user
      // to the same screen instead of redirecting to ShopHomePage.
      MainLayoutScreen: 'main',
      RepairDetailsScreen: 'repair-details',
      HelpScreen: 'help',
    },
  },
};

export default function AppNavigator() {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);

  // Sync scrollbar thumb colour with the active brand theme (web only).
  // Writes `--scrollbar-thumb` on :root; index.web.css picks it up globally.
  useScrollbarTheme();

  // Bootstrap: hydrate all Redux slices from secureStorage before rendering,
  // mirroring the native AppNavigator pattern so deep-link refreshes work.
  useEffect(() => {
    const bootstrap = async () => {
      try {
        await rehydrateAgencyStorageWeb();
        await handleAgencyFromUrl(dispatch);

        const savedLanguage =
          (await secureStorage.getItem(LANGUAGE_STORAGE_KEY)) ??
          (await storageService.getLanguage());
        if (savedLanguage) {
          dispatch(setLanguage(savedLanguage));
        }

        const savedColorMode = await storageService.getColorMode();
        if (savedColorMode) { dispatch(setColorMode(savedColorMode as ColorMode)); }

        const rawToken = await storageService.getAuthToken();
        const token = await probeBootstrapOidcSession(rawToken);

        if (token) {
          await hydrateWebReduxState(dispatch);
        }

        try {
          await migrateLocalStorageKeys();
        } catch {
          /* ignore — storage may be unavailable */
        }

        dispatchPendingSuccessToast(dispatch);
      } catch (err) {
        console.error(
          '[AppNavigator.web] bootstrap() error — rendering app without hydration:',
          err,
        );
      } finally {
        setIsReady(true);
      }
    };
    bootstrap();
  }, [dispatch]);

  /**
   * Called once the NavigationContainer is mounted and ready.
   * If a stored access token exists (session still valid) we skip AgencySelectionScreen
   * and navigate straight to ShopHomePage — no OIDC redirect required.
   */
  const handleNavigationReady = useCallback(async () => {
    let token = await storageService.getAuthToken();

    let justLoggedOut = false;
    try {
      justLoggedOut = sessionStorage.getItem('JUST_LOGGED_OUT') === '1';
      if (justLoggedOut) {
        sessionStorage.removeItem('JUST_LOGGED_OUT');
        console.log(
          '[AppNavigator.web] JUST_LOGGED_OUT flag detected — skipping auth probe',
        );
      }
    } catch {
      /* sessionStorage unavailable */
    }

    if (!token && !justLoggedOut) {
      try {
        const probed = await probeOidcSession(userManager, storageService);
        if (probed) {
          token = probed;
        }
      } catch (e) {
        console.warn(
          '[AppNavigator.web] Failed to recover token from auth session:',
          e,
        );
      }
    }

    if (!token) {
      return;
    }

    const pathname = globalThis.window.location.pathname.replace(/\/$/, '');
    const authenticatedPaths = [
      '/ShopHomePage',
      '/main',
      '/repair-details',
      '/help',
    ];

    if (
      authenticatedPaths.some(
        p => pathname === p || pathname.startsWith(p + '/'),
      )
    ) {
      console.log(
        `[AppNavigator.web] Stored token found — staying on URL path: ${pathname}`,
      );
    } else {
      console.log(
        '[AppNavigator.web] Stored token found — navigating to ShopHomePage',
      );
      requestAnimationFrame(() => navigateToShopWhenReady(navigationRef));
    }
  }, []);

  // ── Proactive TTAuth cookie refresh ──────────────────────────────────────
  // The TTAuth session cookie (__ttauthpreprod) has a ~1-hour server-side TTL.
  // Without proactive renewal, the cookie expires silently while the user is
  // idle on a screen (e.g. RepairDetails for 1 hour).  The next API call then
  // fires without a valid cookie, receives a server error, and the axios 401
  // interceptor triggers the reactive /cookie/me fetch — but the first request
  // has already failed and the error is visible to the user.
  //
  // This effect closes the gap by:
  //   1. Scheduling a 50-minute interval to renew the cookie before it expires.
  //   2. Listening for visibilitychange so a refresh fires the moment the user
  //      returns to the tab after it was backgrounded for an extended period.
  //
  // Both paths call refreshAccessToken() (ttAuthAdapter.web → /cookie/me) only
  // when the user is authenticated (token present) and has not just logged out.
  // The axiosInstance isRefreshing mutex handles any accidental concurrency with
  // the reactive interceptor path.
  useEffect(() => {
    // Time in ms between proactive refreshes.  50 min < 60-min cookie TTL.
    const PROACTIVE_REFRESH_INTERVAL_MS = 50 * 60 * 1000;
    // Minimum idle time (ms) before a visibilitychange re-triggers a refresh.
    // Avoids hammering /cookie/me when the user rapidly alt-tabs.
    const VISIBILITY_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

    let lastRefreshAt = Date.now();

    const doProactiveRefresh = async (reason: string) => {
      // Skip if JUST_LOGGED_OUT (user intentionally signed out in this tab).
      try {
        if (sessionStorage.getItem('JUST_LOGGED_OUT') === '1') {
          return;
        }
      } catch {
        /* ignore */
      }

      // Only refresh when the user is authenticated.
      const token = await storageService.getAuthToken();
      if (!token) {
        return;
      }

      try {
        console.log(
          `[AppNavigator.web] Proactive token refresh — reason: ${reason}`,
        );
        const newToken = await refreshAccessToken();
        if (newToken) {
          lastRefreshAt = Date.now();
          console.log('[AppNavigator.web] Proactive token refresh succeeded');
        } else {
          // Refresh returned null — session fully expired; let the next API call
          // trigger the 401 interceptor path which calls onSessionExpired().
          console.warn(
            '[AppNavigator.web] Proactive token refresh returned null — session may be expired',
          );
        }
      } catch (err) {
        // Non-fatal: the 401 interceptor is the safety net.
        console.warn('[AppNavigator.web] Proactive token refresh threw:', err);
      }
    };

    // 1. Periodic interval — fires every 50 minutes regardless of user activity.
    const intervalId = setInterval(() => {
      doProactiveRefresh('50-min interval');
    }, PROACTIVE_REFRESH_INTERVAL_MS);

    // 2. visibilitychange — fires when the user switches back to this tab.
    //    Only triggers a refresh if >= VISIBILITY_REFRESH_THRESHOLD_MS have
    //    elapsed since the last refresh (avoids redundant calls).
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const msSinceLastRefresh = Date.now() - lastRefreshAt;
        if (msSinceLastRefresh >= VISIBILITY_REFRESH_THRESHOLD_MS) {
          doProactiveRefresh(
            `visibilitychange (idle ${Math.round(
              msSinceLastRefresh / 60000,
            )} min)`,
          );
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Register the global session-expired handler.
    // Called by the axios interceptor when both access and refresh tokens are fully expired.
    // Clears auth state (setForceReauth is included in signoutWithBrowser) and returns
    // the user to AgencySelectionScreen where they must sign in again.
    setSessionExpiredCallback(async () => {
      console.warn(
        '[AppNavigator] Session fully expired — redirecting to AgencySelectionScreen',
      );
      tabManager.broadcastSessionExpired();
      await signoutWithBrowser();
      // signoutWithBrowser on web does window.location.href = origin, which reloads the app
      // and lands on AgencySelectionScreen. No further navigation needed here.
    });

    // Listen for cross-tab auth events (SESSION_EXPIRED / SIGNED_OUT broadcast from
    // another tab) and sign this tab out immediately so all tabs stay in sync.
    const unsubAuthEvent = tabManager.onAuthEvent(async event => {
      if (event === 'SESSION_EXPIRED' || event === 'SIGNED_OUT') {
        await signoutWithBrowser();
      }
    });

    return () => {
      unsubAuthEvent();
    };
  }, []);

  // Don't render until secureStorage bootstrap is done — prevents stale state flash
  if (!isReady) {
    return null;
  }

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      onReady={handleNavigationReady}
      onStateChange={() => {
        // Track SPA route changes in Pendo.
        // window.location.href reflects the updated URL at this point because
        // React Navigation has already called window.history.pushState/replaceState.
        pendoService.pageLoad(globalThis.window.location.href);
      }}
      documentTitle={{
        formatter: (options, route) => {
          const screenTitles: Record<string, string> = {
            AgencySelectionScreen: 'Agency Selection',
            ShopHomePage: 'Shop Home',
            MainLayoutScreen: 'Work Orders',
            RepairDetailsScreen: 'Repair Details',
            HelpScreen: 'Help',
          };
          const screenName = route?.name ?? '';
          const screenTitle = screenTitles[screenName] ?? screenName;
          return `${screenTitle} — Faster Technician`;
        },
      }}>
      {/* §2.1 — Suspense boundary wraps the entire navigator so lazy chunks can load */}
      <Suspense fallback={<ScreenFallback />}>
        <Stack.Navigator
          initialRouteName="AgencySelectionScreen"
          screenOptions={{
            headerShown: false,
          }}>
          {/* Authentication and onboarding screens */}
          <Stack.Screen
            name="AgencySelectionScreen"
            component={AgencySelectionScreen}
            options={{
              header: AgencySelectionHeaderWeb,
              headerShown: true,
            }}
          />

          {/* Main application screens */}
          <Stack.Screen
            name="ShopHomePage"
            component={ShopHomePage}
            options={{
              header: ShopHomePageHeaderWeb,
              headerShown: true,
            }}
          />
          <Stack.Screen
            name="MainLayoutScreen"
            component={MainLayoutScreen}
            options={{
              headerShown: true,
              gestureEnabled: false,
            }}
          />
          <Stack.Screen name="SignatureScreen" component={SignatureScreen} />
          <Stack.Screen
            name="workOrdersScreen"
            component={WorkOrdersScreen}
            options={{headerShown: false}}
          />
          <Stack.Screen
            name="partsScreen"
            component={PartsScreen}
            options={{headerShown: false}}
          />
          <Stack.Screen
            name="RepairDetailsScreen"
            component={RepairDetailsScreen}
            options={{headerShown: false}}
          />
          <Stack.Screen
            name="HelpScreen"
            component={HelpScreen}
            options={{
              headerShown: true,
              gestureEnabled: false,
            }}
          />
        </Stack.Navigator>
      </Suspense>
    </NavigationContainer>
  );
}
